/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: Frequently Asked Questions section for AI Product Avatar
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* Question Row */}
        <div className="flex items-center justify-between gap-4">
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          {/* +/- Icon */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Answer - Shown when expanded */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'How does the product avatar technology work?',
      answer:
        'FalcoCut uses advanced vision-to-video AI to understand your product image, match it with an avatar, and generate natural interactions such as holding, showcasing, or explaining the item. The system analyzes hand positions, angles, and lighting to make the product appear naturally integrated—not pasted on. Combined with AI voice, lip-sync, and gesture modeling, the final video looks polished and consistent across all your product demos.',
    },
    {
      id: 'faq-2',
      question: 'What product avatars can I generate?',
      answer:
        'FalcoCut supports a wide range of product avatar styles, from friendly UGC presenters to more polished, professional spokespersons. You can generate avatars that hold small items like cosmetics or jewelry, mid-sized items like gadgets or accessories, or larger products such as home goods or appliances. The AI automatically adjusts scale, placement, and gestures so the avatar interacts with your product naturally in any scene.',
    },
    {
      id: 'faq-3',
      question: 'How do I create AI avatars holding products?',
      answer: (
        <>
          <p className="mb-3">FalcoCut guides you through a simple three-step flow.</p>
          <ol className="list-decimal list-inside space-y-2 mb-3">
            <li>
              <strong>Choose an avatar and upload a clear photo of your product</strong>{' '}
              so the system understands what the avatar should interact with.
            </li>
            <li>
              <strong>
                Add the voiceover content and set the overall look of the video,
              </strong>{' '}
              including your preferred subtitles, background, and format.
            </li>
            <li>
              <strong>Pick how the avatar should hold or present your product,</strong>{' '}
              then let the AI generate the full clip for you. When it&apos;s done, you can
              download the finished video instantly.
            </li>
          </ol>
          <p>
            The entire workflow takes only a few minutes and doesn&apos;t require any
            filming, equipment, or editing experience.
          </p>
        </>
      ),
    },
    {
      id: 'faq-4',
      question: 'Can I customize my own brand avatar?',
      answer:
        "Absolutely. FalcoCut lets you customize your avatar's appearance, tone, voice, and presentation style so it aligns with your brand identity. You can adjust clothing style, personality, delivery tone, and even the way the avatar interacts with your product. This ensures every video feels cohesive and on-brand, whether you're creating UGC-style demos or polished product explainers.",
    },
    {
      id: 'faq-5',
      question: 'Do FalcoCut AI Avatars support multiple languages?',
      answer:
        'They do. FalcoCut avatars can speak in 30+ languages and dialects, supported by natural-sounding voices and precise lip-sync. This makes it easy to produce content for different regions without hiring multilingual presenters. You can combine any language with your preferred avatar look and voice style, allowing you to create region-specific product videos that feel native to each market—ideal for Amazon listings, TikTok Shop, and global ecommerce campaigns.',
    },
    {
      id: 'faq-6',
      question: 'How long does the video production process take?',
      answer:
        'FalcoCut generates most product demo videos in just a few minutes. Shorter videos are often ready in under 10 minutes, while custom avatars may require additional time during their initial setup. Once your avatar is ready, producing new videos is fast and efficient. Compared to traditional production timelines that involve scheduling, filming, and editing, FalcoCut lets you create and publish new content with remarkable speed.',
    },
    {
      id: 'faq-7',
      question: 'Can I revise or update the video easily?',
      answer:
        'Yes. FalcoCut makes revisions simple and fast. You can update the script, change the avatar, swap product images, adjust placement, or generate new variations without reshooting anything. This is especially helpful when packaging changes, new features are added, or you want to test multiple hooks for ads. Regenerating a revised video usually takes just a few minutes.',
    },
    {
      id: 'faq-8',
      question: 'Do I need filming, equipment, or technical skills?',
      answer:
        'Not at all. FalcoCut is designed for anyone to use—no cameras, studios, lighting, or editing experience required. You simply upload your product image, choose an avatar, and let the AI handle the rest. The platform automates script delivery, gestures, voice, lip-sync, and video rendering, making professional-quality product demos accessible to solo sellers and large teams alike.',
    },
    {
      id: 'faq-9',
      question:
        'Is there a best product video generator for e-commerce and social platforms?',
      answer:
        "FalcoCut is a strong choice for sellers who need quick, clear product videos without dealing with filming or editing. You can upload your product image, pick an avatar, add your script, and get a ready-to-use video in minutes. The videos are sized and formatted for platforms like Amazon, TikTok Shop, and Meta, so you don't have to adjust anything yourself. FalcoCut also supports multiple languages and flexible avatar options, making it easy to create videos for different markets. If you want a simple way to produce steady, reliable product content at scale, FalcoCut is built for that.",
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ List - 840px width centered */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
